import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  Context,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  NextLink,
  Observable,
  Operation,
} from '@apollo/client';
import { OperationDefinitionNode } from 'graphql';

import { CustomContextVars, ApolloMiddleware } from '@nxte-nl/apollo-hooks';
import { ErrorBoundary } from '@nxte-nl/error-boundary';

import { apolloMiddlewareHooks } from '../../hooks/apolloMiddlewareHooks/apolloMiddlewareHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { modalHooks } from '../../hooks/modalHooks/modalHooks';
import { queryStringHooks } from '../../hooks/queryStringHooks/queryStringHooks';
import { redeemHooks } from '../../hooks/redeemHooks/redeemHooks';
import { GlobalMessages, GlobalSignals } from '../../types/Messages';
import { BaseResponse } from '../../types/apiCompoundCommon';
import { languageUtils } from '../../utils/languageUtils/languageUtils';

type UseClientUpdateParams = {
  clientRef: React.MutableRefObject<ApolloClient<any> | null>;
  languageRef: React.MutableRefObject<string>;
  redeemTokenRef: React.MutableRefObject<string | null>;
};

export const callbacks = {
  apolloRequestHandler: (
    redeemTokenRef: React.MutableRefObject<string | null>,
    language: string,
    defaultErrorMiddleware: ApolloMiddleware,
  ) => (operation: Operation, forward: NextLink): Observable<FetchResult> | null => {
    const languageSliced = language === '' ? languageUtils.getDefaultLanguage() : language;
    operation.setContext((context: Context) => {
      const updatedHeaders = {
        'x-store': CONFIG.cadeaubonGraphql.storeID.toString(),
        'x-lang': languageSliced,
        'x-country': CONFIG.cadeaubonGraphql.country,
        'x-pathname': window.location.pathname + window.location.search,
        ...context.headers,
      };

      const operationDefinition = operation.query.definitions.find(
        x => x.kind === 'OperationDefinition',
      ) as OperationDefinitionNode;
      if (operationDefinition.operation === 'mutation') {
        updatedHeaders['x-bypass-mutations'] = '1';
      }

      return {
        headers: updatedHeaders,
      };
    });

    operation.variables.userData = {
      language: languageSliced,
      storeID: CONFIG.cadeaubonGraphql.storeID,
      countryCode: CONFIG.cadeaubonGraphql.country,
    };

    const redeemToken = operation.getContext().redeemToken;

    if (redeemToken) {
      operation.variables.userData.redeemToken = redeemToken;
    } else if (redeemTokenRef.current !== null) {
      operation.variables.userData.redeemToken = redeemTokenRef.current;
    }

    return forward(operation)
      .map((data: FetchResult<BaseResponse<unknown> | any>) => {
        const context: CustomContextVars = operation.getContext();
        if (context.middleware) return context.middleware<unknown>(data);
        if (!context.ignoreError) return defaultErrorMiddleware<unknown>(data);
        return data;
      });
  },
};

export const hooks = {
  useRedeemToken() {
    const { redeemToken: redeemTokenFromQuery } = queryStringHooks.useParsedQueryString();
    const redeemToken = redeemHooks.useGetRedeemToken();

    return React.useMemo(() => {
      return redeemTokenFromQuery ? redeemTokenFromQuery : redeemToken ? redeemToken : null;
    }, [redeemToken, redeemTokenFromQuery]);
  },

  useGetStates() {
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    const languageRef = React.useRef<string>(language);

    return React.useMemo(() => ({
      language,
      languageRef,
    }), [language]);
  },

  useClientUpdate({
    clientRef,
    redeemTokenRef,
    languageRef,
  }: UseClientUpdateParams) {
    const location = useLocation();
    const defaultErrorMiddleware = apolloMiddlewareHooks.useDefaultErrorMiddleware();

    React.useEffect(() => {
      if (clientRef.current === null) {
        clientRef.current = new ApolloClient({
          link: from([
            new ApolloLink((operation: Operation, forward: NextLink) =>
              callbacks.apolloRequestHandler(
                redeemTokenRef, languageRef.current, defaultErrorMiddleware,
              )(
                operation,
                forward,
              )),
            new HttpLink({
              uri: localStorage.getItem('cbApiUrl') || CONFIG.cadeaubonGraphql.uri,
              credentials: 'include',
            }),
          ]),
          cache: new InMemoryCache(),
          defaultOptions: {
            query: {
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
            },
            mutate: {
              fetchPolicy: 'no-cache',
            },
            watchQuery: {
              fetchPolicy: 'no-cache',
            },
          },
        });
      }
    }, [clientRef, defaultErrorMiddleware, languageRef, location.pathname, redeemTokenRef]);
  },

  useUpdateLanguageRef(languageRef: React.MutableRefObject<string>, language: string) {
    React.useEffect(() => {
      languageRef.current = language;
    }, [language, languageRef]);
  },
};

export type Props = {
  children?: React.ReactNode;
};

export const AppContainer: React.FC<Props> = React.memo(({ children }) => {
  const { languageRef, language } = hooks.useGetStates();

  const redeemToken = hooks.useRedeemToken();

  const onRedeemTokenUpdated = messageHooks.useNextSignal(GlobalSignals.RedeemTokenUpdated);

  const redeemTokenRef = React.useRef<string | null>(redeemToken as string);

  React.useEffect(() => {
    redeemTokenRef.current = redeemToken as string | null;
    onRedeemTokenUpdated(redeemTokenRef.current);
  }, [onRedeemTokenUpdated, redeemToken]);

  const clientRef = React.useRef<ApolloClient<any> | null>(null);

  hooks.useUpdateLanguageRef(languageRef, language);

  hooks.useClientUpdate({
    clientRef,
    redeemTokenRef,
    languageRef,
  });

  modalHooks.useDisableScrolling();

  return clientRef.current ? (
    <ApolloProvider client={clientRef.current}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </ApolloProvider>
  ) : null;
});

AppContainer.displayName = 'AppContainer';
